import { useCallback, useEffect, useState } from "react";
import { CalendarClock, ChevronRight, LoaderCircle, Plus, Search } from "lucide-react";

import { getBookings } from "./api";
import { BookingPanel } from "./BookingPanel";
import type { BookingRequest } from "./types";

export function BookingsPage({ canWrite }: { canWrite: boolean }) {
  const [bookings, setBookings] = useState<BookingRequest[]>([]);
  const [cursor, setCursor] = useState<string | null>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string>();
  const [creating, setCreating] = useState(false);

  const load = useCallback(async (next?: string, query = "") => {
    setLoading(true);
    try {
      const page = await getBookings({ cursor: next, search: query || undefined });
      setBookings((current) => (next ? [...current, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);

  return (
    <div className="page-content bookings-page">
      <header className="page-intro client-intro">
        <div>
          <span className="eyebrow lime">Booking requests</span>
          <h1>Turn every request into a careful next step.</h1>
          <p>
            Capture the customer’s preferences, review the details, and confirm only after the team
            has validated availability.
          </p>
        </div>
        {canWrite ? (
          <button className="primary-button compact-action" onClick={() => setCreating(true)}>
            <Plus /> New request
          </button>
        ) : null}
      </header>
      <section className="request-notice">
        <CalendarClock />
        <div>
          <strong>Request-first workflow</strong>
          <span>Nothing here is an automatically confirmed appointment.</span>
        </div>
      </section>
      <section className="content-card">
        <div className="section-heading booking-toolbar">
          <div>
            <span className="eyebrow">Operations queue</span>
            <h2>All requests</h2>
          </div>
          <form
            className="search-shell real-search"
            onSubmit={(event) => {
              event.preventDefault();
              void load(undefined, search);
            }}
          >
            <Search />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Customer, address, service…"
              aria-label="Search booking requests"
            />
          </form>
        </div>
        {failed ? (
          <div className="load-error" role="alert">
            We couldn’t refresh requests.
            <button onClick={() => void load(undefined, search)}>Try again</button>
          </div>
        ) : null}
        <BookingTable
          bookings={bookings}
          loading={loading && bookings.length === 0}
          onOpen={setSelected}
        />
        {cursor ? (
          <button className="secondary-button load-more" onClick={() => void load(cursor, search)}>
            {loading ? <LoaderCircle className="spin" /> : null} Load more
          </button>
        ) : null}
      </section>
      {creating || selected ? (
        <BookingPanel
          bookingId={selected}
          canWrite={canWrite}
          onClose={() => {
            setCreating(false);
            setSelected(undefined);
          }}
          onChanged={async () => {
            setCreating(false);
            setSelected(undefined);
            await load(undefined, search);
          }}
        />
      ) : null}
    </div>
  );
}

function BookingTable({
  bookings,
  loading,
  onOpen,
}: {
  bookings: BookingRequest[];
  loading: boolean;
  onOpen: (id: string) => void;
}) {
  if (loading)
    return (
      <div className="table-state">
        <LoaderCircle className="spin" /> Loading requests…
      </div>
    );
  if (bookings.length === 0)
    return <div className="table-state">No booking requests match this view.</div>;
  return (
    <div className="table-wrap">
      <table className="mobile-card-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Service</th>
            <th>Preferred visit</th>
            <th>Status</th>
            <th>
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking) => (
            <tr key={booking.id}>
              <td data-label="Customer">
                <strong>{booking.clientName ?? "Unlinked request"}</strong>
                <span>{place(booking)}</span>
              </td>
              <td data-label="Service">{booking.serviceName}</td>
              <td data-label="Preferred visit">{preferredVisit(booking)}</td>
              <td data-label="Status">
                <span className={`status request-status request-${booking.status.toLowerCase()}`}>
                  {booking.status.replaceAll("_", " ")}
                </span>
              </td>
              <td data-label="">
                <button
                  className="row-action"
                  aria-label={`Open request for ${booking.clientName ?? booking.serviceName}`}
                  onClick={() => onOpen(booking.id)}
                >
                  <ChevronRight />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function place(booking: BookingRequest) {
  return booking.propertyAddress
    ? `${booking.propertyAddress}${booking.propertyCity ? ` · ${booking.propertyCity}` : ""}`
    : "No property linked";
}
function preferredVisit(booking: BookingRequest) {
  if (!booking.preferredDate) return "Flexible date";
  const label = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${booking.preferredDate}T12:00:00`));
  return booking.preferredWindowStart && booking.preferredWindowEnd
    ? `${label} · ${booking.preferredWindowStart.slice(0, 5)}–${booking.preferredWindowEnd.slice(0, 5)}`
    : label;
}
